
import { CartItem, Order, ReturnRequest, OrderStatus } from '../types';
import { storage } from '../lib/storage';
import { loyaltyService } from './loyaltyService';
import { taxService } from './taxService';
import { shippingService } from './shippingService';
import { couponService } from './couponService';
import { influencerService } from './influencerService';
import { vipService } from './vipService';

export const orderService = {
  async createOrder(storeId: string, items: CartItem[], customerId: string, paymentMethod: 'CARD' | 'UPI' | 'COINS' = 'CARD', coinsRedeemed: number = 0, shippingMethodId?: string, couponId?: string, influencerId?: string) {
    const subtotal = items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
    
    // 1. Coupon Discount
    let discountAmount = 0;
    if (couponId) {
      const allCoupons = localStorage.getItem('lynqar_coupons');
      const coupons = allCoupons ? JSON.parse(allCoupons) : [];
      const coupon = coupons.find((c: any) => c.id === couponId);
      if (coupon) {
        discountAmount = couponService.calculateDiscount(coupon, subtotal);
        couponService.incrementUsage(couponId);
      }
    }

    // 2. VIP Discount (Based on lifetime spend before this order)
    let vipDiscountAmount = 0;
    let currentVIPTier = 'NONE';
    if (customerId !== 'guest-user' && customerId !== 'guest-session') {
      const vipTier = vipService.getCurrentTier(storeId, customerId);
      if (vipTier) {
        currentVIPTier = vipTier.level;
        vipDiscountAmount = (subtotal - discountAmount) * (vipTier.discountPercentage / 100);
      }
    }

    // 3. Loyalty Coin Discount
    const coinDiscount = loyaltyService.calculateRedemptionValue(coinsRedeemed);
    const amountAfterBaseDiscounts = subtotal - discountAmount - vipDiscountAmount - coinDiscount;
    
    const platformConfig = storage.getPlatformConfig();
    const store = storage.getStoreConfig(storeId);
    
    // 4. Shipping Calculation
    const methods = shippingService.getStoreMethods(storeId);
    const selectedMethod = methods.find(m => m.id === shippingMethodId) || methods[0];
    const shippingCost = shippingService.calculateCost(selectedMethod, subtotal);

    // 5. Tax Calculation
    const taxRate = store.taxConfig?.enabled ? (store.taxConfig.taxRate || 0) : 0;
    const taxAmount = taxService.calculateTax(amountAfterBaseDiscounts, taxRate);
    const total = amountAfterBaseDiscounts + taxAmount + shippingCost;
    
    // Commission logic (calculated on final paid amount)
    const rate = store.commissionRate !== undefined ? store.commissionRate : platformConfig.globalCommissionRate;
    const platformCommission = total * rate;
    const sellerNet = total - platformCommission;
    
    const coinsEarned = loyaltyService.calculateEarnedCoins(total);
    
    // Influencer logic
    let influencerCommission = 0;
    if (influencerId) {
       const comm = influencerService.trackConversion(influencerId, total);
       if (comm) influencerCommission = comm;
    }

    try {
      const allOrders = storage.getOrders();
      const newOrder: Order = {
        id: 'ORD-' + Math.floor(100000 + Math.random() * 900000),
        storeId: storeId,
        userId: customerId,
        subtotal: subtotal,
        taxAmount: taxAmount,
        taxRate: taxRate,
        total: total,
        platformCommission,
        sellerNet: sellerNet - influencerCommission, // Subtract influencer comm from seller net
        status: 'PAID', 
        paymentMethod: paymentMethod,
        createdAt: new Date().toISOString(),
        items: [...items],
        coinsEarned,
        coinsRedeemed,
        shippingMethodId: selectedMethod.id,
        shippingCost,
        couponId,
        discountAmount,
        influencerId,
        influencerCommission,
        vipDiscountAmount,
        vipTierAtPurchase: currentVIPTier as any
      };

      storage.saveOrders([newOrder, ...allOrders]);
      
      // Update inventory
      const allProducts = storage.getProducts();
      const updatedProducts = allProducts.map(p => {
        const cartItem = items.find(item => item.productId === p.id);
        if (cartItem) {
          return { ...p, stock: Math.max(0, p.stock - cartItem.quantity) };
        }
        return p;
      });
      storage.saveProducts(updatedProducts);

      // Handle Coins
      if (customerId !== 'guest-user' && customerId !== 'guest-session') {
        if (coinsRedeemed > 0) {
          loyaltyService.addTransaction(customerId, -coinsRedeemed, 'REDEEM', `Redeemed on order ${newOrder.id}`, newOrder.id);
        }
        if (coinsEarned > 0) {
          loyaltyService.addTransaction(customerId, coinsEarned, 'EARN', `Earned from order ${newOrder.id}`, newOrder.id);
        }
      }

      return { success: true, orderId: newOrder.id };
    } catch (error) {
      console.error("Order process failed:", error);
      return { success: false, error };
    }
  },

  async getStoreOrders(storeId: string): Promise<Order[]> {
    const all = storage.getOrders();
    return all.filter(o => o.storeId === storeId);
  },

  async getOrderById(orderId: string): Promise<Order | null> {
    const all = storage.getOrders();
    return all.find(o => o.id === orderId) || null;
  },

  async requestReturn(orderId: string, reason: string) {
    const order = await this.getOrderById(orderId);
    if (!order) throw new Error("Order not found");
    if (order.status !== 'DELIVERED') throw new Error("Only delivered orders can be returned.");

    const newReturn: ReturnRequest = {
      id: 'RET-' + Date.now(),
      orderId: order.id,
      storeId: order.storeId,
      reason,
      status: 'PENDING',
      requestedAt: new Date().toISOString(),
      items: order.items || []
    };

    storage.saveReturnRequest(newReturn);

    // Update Order Status
    const allOrders = storage.getOrders();
    const updated = allOrders.map(o => o.id === orderId ? { ...o, status: 'RETURN_REQUESTED' as OrderStatus } : o);
    storage.saveOrders(updated);

    return newReturn;
  },

  async processReturn(returnId: string, status: ReturnRequest['status']) {
    const returns = storage.getReturns();
    const req = returns.find(r => r.id === returnId);
    if (!req) return;

    req.status = status;
    req.processedAt = new Date().toISOString();
    storage.saveReturnRequest(req);

    if (status === 'REFUNDED') {
      const allOrders = storage.getOrders();
      const updated = allOrders.map(o => o.id === req.orderId ? { ...o, status: 'RETURNED' as OrderStatus } : o);
      storage.saveOrders(updated);
      
      // Optional: Add logic for restocking items
      const allProducts = storage.getProducts();
      const updatedProducts = allProducts.map(p => {
        const returnedItem = req.items.find(i => i.productId === p.id);
        if (returnedItem) {
          return { ...p, stock: p.stock + returnedItem.quantity };
        }
        return p;
      });
      storage.saveProducts(updatedProducts);
    }
  }
};
